import React from 'react'
import { GraduationCap, Briefcase, Plane, Code, Database, Brain, Lightbulb } from 'lucide-react'

const About = () => {
  const education = [
    {
      title: "985计算机本科",
      description: "计算机专业背景，奠定扎实的技术基础",
      icon: GraduationCap,
      color: "text-blue-600"
    },
    {
      title: "香港理工大学",
      description: "校级优秀交换生，拓展国际视野",
      icon: GraduationCap,
      color: "text-green-600"
    },
    {
      title: "加州伯克利UCB大学",
      description: "暑期交流访问学者，接触前沿技术理念",
      icon: GraduationCap,
      color: "text-yellow-600"
    },
    {
      title: "加拿大UBC大学",
      description: "实验室科研助理，体验国际化学术环境",
      icon: GraduationCap,
      color: "text-red-600"
    }
  ]

  const experience = [
    {
      title: "电商平台算法工程师",
      description: "负责搜索和搜索广告算法优化，提升用户体验和商业价值",
      icon: Database,
      period: "2019-2021",
      skills: ["搜索算法", "推荐系统", "机器学习", "数据分析"]
    },
    {
      title: "OTA平台算法工程师",
      description: "专注内容推荐算法，为用户提供个性化旅行体验",
      icon: Code,
      period: "2021-2022",
      skills: ["推荐算法", "内容理解", "用户画像", "A/B测试"]
    },
    {
      title: "AI全栈工程师 & 创业者",
      description: "探索AI时代的技术创新，构建智能应用解决方案，积极参与创业项目和黑客松比赛",
      icon: Brain,
      period: "2024-至今",
      skills: ["大语言模型", "全栈开发", "AI应用", "产品设计", "创业实践"]
    }
  ]

  const journey = [
    {
      title: "自驾中国",
      description: "勇敢裸辞离开舒适圈，开启人生新篇章，用车轮丈量祖国大地",
      icon: Plane,
      color: "text-blue-600"
    },
    {
      title: "环球旅行",
      description: "走向世界各地，体验不同国家和地区的文化魅力，走遍六大洲，感受文化多样性",
      icon: Briefcase,
      color: "text-green-600"
    },
    {
      title: "南北极探险",
      description: "跟随国家地理探险队的南极探险，挑战极地环境，探索地球的最后边界",
      icon: Plane,
      color: "text-cyan-600"
    },
    {
      title: "创业实践",
      description: "积极参与黑客松比赛，开发创新产品，探索AI时代的商业机会",
      icon: Lightbulb,
      color: "text-orange-600"
    }
  ]

  return (
    <section id="about" className="section-padding bg-white">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="gradient-text mb-4">关于我</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            从传统算法工程师到AI时代探索者，从办公室白领到环球旅居的数字游民，从创新产品开发到创业实践的探索者，我的人生轨迹充满了转折与惊喜
          </p>
        </div>

        {/* 教育背景 */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">教育背景</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {education.map((edu, index) => (
              <div key={index} className="card text-center hover:scale-105 transform transition-all duration-300">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4 ${edu.color}`}>
                  <edu.icon size={32} />
                </div>
                <h4 className="font-semibold mb-2 text-gray-800">{edu.title}</h4>
                <p className="text-gray-600 text-sm">{edu.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 职业经历 */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">职业经历</h3>
          <div className="space-y-8">
            {experience.map((exp, index) => (
              <div key={index} className="card hover:shadow-2xl transition-all duration-300">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                      <exp.icon size={32} className="text-primary-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                      <h4 className="text-xl font-semibold text-gray-800">{exp.title}</h4>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        {exp.period}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4">{exp.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {exp.skills.map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 人生转折 */}
        <div>
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">人生转折</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {journey.map((step, index) => (
              <div key={index} className="card text-center hover:scale-105 transform transition-all duration-300">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4 ${step.color}`}>
                  <step.icon size={32} />
                </div>
                <h4 className="font-semibold mb-2 text-gray-800">{step.title}</h4>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default About 